import { TypeOptions, toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '../../../utils/ApiHelper';
import {
  ContentType,
  DOCUMENT_TYPES,
  LanguageType
} from '../components/Documents/constants/DocumentInterfaces';
import { STORE_KEYS } from '../constants';
import { WaiveFeeActions } from '../constants/EmiSchedule.component';
import {
  FEE_HOLD_SUBMIT_MSG,
  FEE_WAIVE_ERROR_MSG,
  FEE_WAIVE_SUBMIT_MSG
} from '../constants/EmiSchedule.constant';
import { CustomerDocument, Due } from '../interfaces/CaseDetail.type';
import {
  ComponentAdjustment,
  EmiFlowType,
  IFeeWaiveSubmitPayload,
  PendingFeeComponent,
  SendPaymentLinkRequest,
  WaiveFeePayload,
  WaiveFeeType
} from '../interfaces/EmiSchedule.type';
import { PaymentType } from '../interfaces/Payments.type';

import {
  setCaseDetail,
  setCaseDetailLoading,
  setMonthlyEnach,
  setRepaymentDue,
  setRepaymentDueError,
  setRepaymentDueLoading
} from '../reducers/caseDetailSlice';

export const fetchPendingFeeComponents =
  (lan: string, customerRefrenceId: string) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.PENDING_FEE_COMPONENTS, { lan });
    axiosInstance.get(url, { headers: { includeInUptimeMetrics: true } }).then(response => {
      dispatch(
        setCaseDetail({
          lan,
          customerRefrenceId,
          key: STORE_KEYS.PENDING_FEE_COMPONENTS,
          data: response.data
        })
      );
    });
  };

export const submitWaiveFee = (
  lan: string,
  customerReferenceId: string,
  caseReferenceId: string,
  caseBusinessVertical: string,
  payload: { [key: string]: ComponentAdjustment },
  dueDetails: Due,
  phoneNumber: string,
  paymentCallback: (toastMsg: string, msgType: TypeOptions) => void,
  flowType: EmiFlowType,
  phoneNumberCustomId?: string
) => {
  const waiveFeePayload: WaiveFeePayload[] = [];
  let requestType: string;
  const paymentRequests: Array<SendPaymentLinkRequest> = [];
  const feeTypesPaymentRequestsMap: { [key: string]: number } = {};
  for (const dueComponentReferenceId in payload) {
    const { waiverAmount, feeType, ...rest } = payload[dueComponentReferenceId];
    let record: any = {
      ...rest,
      dueReferenceId: dueDetails.referenceId
    };
    if (flowType === EmiFlowType.NEW_FLOW) {
      record = {
        ...record,
        waiverAmount: {
          value: waiverAmount.value,
          currency: 'INR'
        },
        amount: {
          value: waiverAmount.value,
          currency: 'INR'
        }
      };
    }
    waiveFeePayload.push(record);
    if (!feeTypesPaymentRequestsMap[feeType]) {
      feeTypesPaymentRequestsMap[feeType] = 1;
      paymentRequests.push(
        getBasicPaymentRequest(
          lan,
          dueDetails,
          feeType,
          payload[dueComponentReferenceId],
          phoneNumber
        )
      );
      requestType = payload[dueComponentReferenceId].requestType;
    }
  }
  return (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.ADJUST_COMPONENT, { lan });
    axiosInstance.post(url, { dueAdjustmentRequests: waiveFeePayload }).then(() => {
      // TODO: Need to handle due ammount update fail case and reload case
      setTimeout(
        () =>
          getDetailsAndSendPaymentLink(
            lan,
            customerReferenceId,
            caseReferenceId,
            caseBusinessVertical,
            dueDetails,
            paymentRequests,
            paymentCallback,
            dispatch,
            phoneNumber,
            phoneNumberCustomId,
            requestType
          ),
        2000
      );
    });
  };
};

export const getRepaymentDue =
  (lan: string, customerRefrenceId: string, dueReferenceId: string) => (dispatch: Dispatch) => {
    dispatch(
      setRepaymentDueLoading({
        lan,
        customerRefrenceId,
        key: dueReferenceId,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.REPAYMENT_DUE, { dueReferenceId }))
      .then(res => {
        dispatch(
          setRepaymentDue({
            lan,
            customerRefrenceId,
            key: dueReferenceId,
            data: res.data
          })
        );
      })
      .catch(() => {
        dispatch(
          setRepaymentDueError({
            lan,
            customerRefrenceId,
            key: dueReferenceId
          })
        );
      });
  };

const getUpdatedDueFromAllDuesList = (dues: Due[], dueReferenceId: string) => {
  for (let i = 0; i < dues.length; i += 1) {
    if (dues[i].referenceId === dueReferenceId) {
      return dues[i];
    }
  }
  return null;
};

const getBasicPaymentRequest = (
  lan: string,
  dueDetails: Due,
  feeType: keyof typeof WaiveFeeType,
  adjustment: ComponentAdjustment,
  phoneNumber: string
) => {
  const { dueDate } = dueDetails;
  const placeholders: Record<string, string> = {
    due_date: dueDate,
    fee_type: WaiveFeeType[feeType]
  };
  return {
    notifyToAlternateContact: true,
    customAmountProvided: true,
    loanAccountNumber: lan,
    customAmount: {
      amount: 0,
      currency: 'INR'
    },
    templateName: WaiveFeeActions[adjustment.requestType].templateName,
    alternateContactNumber: phoneNumber,
    placeholders
  };
};

const findDueComponentForFeeType = (
  feeType: WaiveFeeType,
  feeComponentAdjustments: PendingFeeComponent[]
) => feeComponentAdjustments.find(component => WaiveFeeType[component.feeType] === feeType);

const getAdjustedAmountFromFeeAdjustment = (
  feeTypeTemplate: string,
  component: PendingFeeComponent
) => {
  if (feeTypeTemplate === WaiveFeeActions.DEFERMENT.templateName) {
    return component?.deferredAmount + component?.deferredGst;
  }
  if (feeTypeTemplate === WaiveFeeActions.HOLD.templateName) {
    return component?.heldAmount + component?.heldGst;
  }
  return 0;
};

const getCompleteSendPaymentLinkRequestFromBasicRequest = (
  request: SendPaymentLinkRequest,
  updatedDue: Due,
  feeComponentAdjustments: PendingFeeComponent[],
  dueDetails: Due
) => {
  const paymentRequest = request;
  let dueComponent = null;
  paymentRequest.customAmount.amount = updatedDue.unpaidDueAmount;
  if (paymentRequest.placeholders.fee_type === WaiveFeeType.LATE_FEE) {
    dueComponent = findDueComponentForFeeType(WaiveFeeType.LATE_FEE, feeComponentAdjustments);
    const waivedAmount =
      dueComponent == null
        ? dueDetails?.totalUnpaidLateFeeAmount + dueDetails?.totalUnpaidLateFeeGst
        : getAdjustedAmountFromFeeAdjustment(paymentRequest.templateName, dueComponent);
    paymentRequest.placeholders.total_waived_amount = waivedAmount.toFixed(2).toString();
  } else if (paymentRequest.placeholders.fee_type === WaiveFeeType.BOUNCE_FEE) {
    dueComponent = findDueComponentForFeeType(WaiveFeeType.BOUNCE_FEE, feeComponentAdjustments);
    const waivedAmount =
      dueComponent == null
        ? dueDetails?.totalUnpaidBounceFeeAmount + dueDetails?.totalUnpaidBounceFeeGst
        : getAdjustedAmountFromFeeAdjustment(paymentRequest.templateName, dueComponent);
    paymentRequest.placeholders.total_waived_amount = waivedAmount.toFixed(2).toString();
  } else if (paymentRequest.placeholders.fee_type === WaiveFeeType.OVERDUE_INTEREST) {
    dueComponent = findDueComponentForFeeType(
      WaiveFeeType.OVERDUE_INTEREST,
      feeComponentAdjustments
    );
    const waivedAmount =
      dueComponent == null
        ? dueDetails?.totalUnpaidPenalInterest
        : getAdjustedAmountFromFeeAdjustment(paymentRequest.templateName, dueComponent);
    paymentRequest.placeholders.total_waived_amount = waivedAmount.toFixed(2).toString();
  }
  return paymentRequest;
};

export const getDetailsAndSendPaymentLink = (
  lan: string,
  customerRefrenceId: string,
  caseReferenceId: string,
  caseBusinessVertical: string,
  dueDetails: Due | undefined,
  sendPaymentLinkRequest: SendPaymentLinkRequest[],
  successCallback: (text: string, type: TypeOptions) => void,
  dispatch: Dispatch,
  phoneNumber: string,
  phoneNumberCustomId?: string,
  requestType?: string
) => {
  dispatch(
    setCaseDetailLoading({
      lan,
      customerRefrenceId,
      key: STORE_KEYS.DETAILS,
      loading: true
    })
  );
  const detailsPromise = axiosInstance.get(getApiUrl(ApiKeys.DETAILS), {
    headers: {
      customerreferenceid: customerRefrenceId,
      loanaccountnumber: lan,
      caseReferenceId,
      caseBusinessVertical
    }
  });
  const pendingFeeUrl = getApiUrl(ApiKeys.PENDING_FEE_COMPONENTS, { lan });
  const pendingFeePromise = axiosInstance.get(pendingFeeUrl, {
    headers: { includeInUptimeMetrics: true }
  });

  Promise.all([detailsPromise, pendingFeePromise]).then(res => {
    const [{ data: caseDetails }, { data: pendingFeeDetails }] = res;
    dispatch(
      setCaseDetail({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.DETAILS,
        data: caseDetails
      })
    );
    dispatch(
      setCaseDetail({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.PENDING_FEE_COMPONENTS,
        data: pendingFeeDetails
      })
    );
    if (!dueDetails) {
      return;
    }
    sendPaymentLink(
      customerRefrenceId,
      lan,
      caseDetails?.dues,
      dueDetails,
      pendingFeeDetails,
      sendPaymentLinkRequest,
      successCallback,
      phoneNumber,
      phoneNumberCustomId,
      requestType
    );
  });
};

const sendPaymentLink = (
  customerReferenceId: string,
  loanAccountNumber: string,
  dues: Due[],
  dueDetails: Due,
  pendingFeeDetails: PendingFeeComponent[],
  requests: SendPaymentLinkRequest[],
  callback: (text: string, type: TypeOptions) => void,
  phoneNumber: string,
  phoneNumberCustomId?: string,
  requestType?: string
) => {
  const updatedDue = getUpdatedDueFromAllDuesList(dues, dueDetails.referenceId);
  if (!updatedDue || updatedDue.unpaidDueAmount === dueDetails.unpaidDueAmount) {
    callback(FEE_WAIVE_ERROR_MSG, 'error');
    return;
  }
  const pendingFeeComponents: PendingFeeComponent[] = pendingFeeDetails || [];
  const componentAdjustments = pendingFeeComponents.filter(
    ({ dueReferenceId: id, feeType }) => id === dueDetails.referenceId && WaiveFeeType[feeType]
  );
  for (let index = 0; index < requests.length; index += 1) {
    const isLastRequest = requests.length - 1 === index;
    const payload = getCompleteSendPaymentLinkRequestFromBasicRequest(
      requests[index],
      updatedDue,
      componentAdjustments,
      dueDetails
    );

    const newPayload = {
      customAmount: payload?.customAmount?.amount,
      loanAccountNumber: payload?.loanAccountNumber,
      type: PaymentType.CUSTOM,
      phoneNumber,
      phoneNumberCustomId: phoneNumberCustomId || ''
    };

    const url = getApiUrl(ApiKeys.SEND_PAYMENT_LINK);
    axiosInstance
      .post(url, newPayload, { headers: { customerReferenceId, loanAccountNumber } })
      .then(() => {
        if (isLastRequest) {
          if (requestType === 'HOLD') {
            callback(FEE_HOLD_SUBMIT_MSG, 'success');
          } else if (requestType === 'WAIVER') {
            callback(FEE_WAIVE_SUBMIT_MSG, 'success');
          }
        }
      })
      .catch(() => callback(FEE_WAIVE_ERROR_MSG, 'error'));
  }
};

export const getEnachPresentDetails =
  (
    customerReferenceId: string,
    loanAccountNumber: string,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  (dispatch: Dispatch) => {
    axiosInstance
      .get(getApiUrl(ApiKeys.MONTHLTY_ENACH_REQUEST), {
        headers: { customerReferenceId, loanAccountNumber, caseReferenceId, caseBusinessVertical }
      })
      .then(res =>
        dispatch(
          setMonthlyEnach({
            lan: loanAccountNumber,
            customerRefrenceId: customerReferenceId,
            data: res.data
          })
        )
      );
  };

export const fetchFeeWaiverHistory = (lan: string, customerId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.FEE_COMPONENT_ADJUSTMENTS, { lan });
  dispatch(
    setCaseDetailLoading({
      lan,
      key: STORE_KEYS.FEE_COMPONENT_ADJUSTMENTS,
      loading: true
    })
  );
  axiosInstance.get(url).then(response => {
    dispatch(
      setCaseDetail({
        lan,
        customerRefrenceId: customerId,
        key: STORE_KEYS.FEE_COMPONENT_ADJUSTMENTS,
        data: response.data,
        loading: false
      })
    );
  });
};

export const fetchCustomerDocs =
  (lan: string, customerId: string, caseReferenceId: string, caseBusinessVertical: string) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CUSTOMER_DOCS);
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId: customerId,
        key: STORE_KEYS.CUSTOMER_DOCS,
        loading: true
      })
    );
    axiosInstance
      .get(url, {
        headers: {
          customerreferenceid: customerId,
          loanaccountnumber: lan,
          caseReferenceId,
          caseBusinessVertical,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId: customerId,
            key: STORE_KEYS.CUSTOMER_DOCS,
            data: response.data.documents,
            loading: false
          })
        );
      });
  };

export const fetchLegalDocs = (lan: string, customerId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.LEGAL_DOCS);
  dispatch(
    setCaseDetailLoading({
      lan,
      customerRefrenceId: customerId,
      key: STORE_KEYS.LEGAL_DOCS,
      loading: true
    })
  );
  axiosInstance
    .get(url, {
      headers: {
        customerreferenceid: customerId,
        loanaccountnumber: lan,
        includeInUptimeMetrics: true
      }
    })
    .then(response => {
      const documents: CustomerDocument[] = response?.data || [];
      const legalDocuments = documents.filter(
        item =>
          [DOCUMENT_TYPES.LDN, DOCUMENT_TYPES.LRN].includes(item.type as DOCUMENT_TYPES) &&
          item.docContentType?.includes(ContentType.PDF) &&
          item.language === LanguageType.ENGLISH
      );

      dispatch(
        setCaseDetail({
          lan,
          customerRefrenceId: customerId,
          key: STORE_KEYS.LEGAL_DOCS,
          data: legalDocuments.length ? legalDocuments?.[0] : {}
        })
      );
    })
    .finally(() => {
      dispatch(
        setCaseDetailLoading({
          lan,
          customerRefrenceId: customerId,
          key: STORE_KEYS.LEGAL_DOCS,
          loading: false
        })
      );
    });
};

export const createEnachRequest = (
  payload: any,
  setLoading?: (loading: boolean) => void,
  successCallBack?: () => void
) => {
  const url = getApiUrl(ApiKeys.CREATE_ENACH_REQUEST);
  const isFunction = typeof setLoading === 'function';
  const isSuccess = typeof successCallBack === 'function';
  if (isFunction) setLoading(true);
  axiosInstance
    .post(url, payload, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
    .then(response => {
      toast.success('ENACH Request raised successfully');
      if (isSuccess) successCallBack();
    })
    .catch(error => {
      toast.error(error.response.data.message);
      logError(error, 'Error while creating Enach request');
    })
    .finally(() => {
      isFunction && setLoading(false);
    });
};

export const getFeeWaiveHistory = async (loanAccountNumber: string) => {
  const url = getApiUrl(ApiKeys.FEE_WAIVER_HISTORY, { loanAccountNumber });
  return axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      if (res.data) {
        return res.data;
      }
      return null;
    })
    .catch((err: Error) => {
      logError(err, 'Error fetching fee waive history');
      throw err?.message;
    });
};

export const submitFeeWaiveV2 = async (
  loanAccountNumber: string,
  payload: IFeeWaiveSubmitPayload
) => {
  const url = getApiUrl(ApiKeys.FEE_WAIVER_V2, { loanAccountNumber });
  return axiosInstance
    .post(url, payload, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      if (res.data) {
        return res.data;
      }
      return null;
    })
    .catch((err: Error) => {
      logError(err, 'Error submitting fee waive');
      throw err?.message;
    });
};
